import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pencil, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Block } from "@/types/proposals";

interface EditableCoverBlockProps {
  block: Block;
  isEditing: boolean;
  onUpdate: (updates: any) => void;
}

export function EditableCoverBlock({ block, isEditing, onUpdate }: EditableCoverBlockProps) {
  const [uploading, setUploading] = useState(false);
  const [editableContent, setEditableContent] = useState(block.content);

  const handleTextChange = (field: string, value: string) => {
    const updated = { ...editableContent, [field]: value };
    setEditableContent(updated);
    onUpdate(updated);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `proposal-photos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('proposals')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('proposals')
        .getPublicUrl(filePath);

      const updated = { ...editableContent, propertyPhoto: publicUrl };
      setEditableContent(updated);
      onUpdate(updated);
      
      toast.success("Photo uploaded successfully!");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const { coverStyle = 'diagonal' } = block.content;

  return (
    <div className="relative">
      {/* Render based on cover style */}
      {renderCoverWithEditing(coverStyle, editableContent, isEditing, handleTextChange, handlePhotoUpload, uploading)}
    </div>
  );
}

function renderCoverWithEditing(
  coverStyle: string,
  content: any,
  isEditing: boolean,
  onChange: (field: string, value: string) => void,
  onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void,
  uploading: boolean
) {
  const {
    companyLogo,
    companyName = "Your Company Name",
    companyTagline = "Professional Roofing Services",
    serviceType = "Roof Replacement",
    date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    propertyPhoto,
    salesRepName = "Sales Representative",
    companyEmail = "info@company.com",
    companyPhone = "(555) 123-4567",
    customerName = "Homeowner Name",
    customerAddress = "123 Main Street",
    customerCityStateZip = "City, ST 12345",
    brandColor = "#2BC5B4",
  } = content;

  const EditableText = ({ value, field, className = "" }: { value: string; field: string; className?: string }) => {
    if (!isEditing) {
      return <span className={className}>{value}</span>;
    }
    return (
      <div className="relative inline-flex items-center gap-2 group">
        <Input
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          className={`${className} border-dashed border-2 border-primary/50`}
        />
        <Pencil className="w-4 h-4 text-primary absolute -left-6 opacity-50" />
      </div>
    );
  };

  // Diagonal Cover (most common)
  if (coverStyle === 'diagonal') {
    return (
      <div className="relative w-full h-full bg-white flex flex-col">
        <div className="flex items-start justify-between p-8 pb-6 relative z-10">
          <div className="flex items-center gap-4 max-w-[60%]">
            {companyLogo && (
              <img src={companyLogo} alt="Company Logo" className="w-16 h-16 object-contain" />
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900 leading-tight">
                <EditableText value={companyName} field="companyName" />
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">{companyTagline}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-gray-900">
              <EditableText value={serviceType} field="serviceType" />
            </div>
            <div className="text-sm text-gray-600 mt-1">{date}</div>
          </div>
        </div>

        <div
          className="absolute top-0 left-0 w-full h-[45%] z-0"
          style={{
            backgroundColor: brandColor,
            opacity: 0.15,
            clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0 100%)',
          }}
        />

        <div className="flex-1 flex items-center justify-center px-12 py-8 relative z-10">
          {propertyPhoto ? (
            <div className="w-full max-w-2xl relative group">
              <img src={propertyPhoto} alt="Property" className="w-full h-auto rounded-lg shadow-2xl object-cover" style={{ maxHeight: '400px' }} />
              {isEditing && (
                <div className="absolute top-4 right-4 flex gap-2">
                  <label className="cursor-pointer">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={uploading}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? "Uploading..." : "Change Photo"}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={onPhotoUpload}
                        className="hidden"
                      />
                    </Button>
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full max-w-2xl h-64 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center relative">
              <p className="text-gray-400 text-sm">Property Photo</p>
              {isEditing && (
                <div className="absolute top-4 right-4 flex gap-2">
                  <label className="cursor-pointer">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={uploading}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? "Uploading..." : "Change Photo"}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={onPhotoUpload}
                        className="hidden"
                      />
                    </Button>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-8 px-8 pb-8 pt-4 border-t border-gray-200 relative z-10">
          <div className="space-y-1.5">
            <h3 className="font-bold text-gray-900 text-base mb-2">{companyName}</h3>
            <p className="text-sm text-gray-700"><span className="font-medium">Sales Rep:</span> {salesRepName}</p>
            <p className="text-sm text-gray-700"><span className="font-medium">Email:</span> {companyEmail}</p>
            <p className="text-sm text-gray-700"><span className="font-medium">Phone:</span> {companyPhone}</p>
          </div>
          <div className="space-y-1.5">
            <h3 className="font-bold text-gray-900 text-base mb-2">Prepared For</h3>
            <p className="text-sm text-gray-700 font-medium">
              <EditableText value={customerName} field="customerName" />
            </p>
            <p className="text-sm text-gray-700">
              <EditableText value={customerAddress} field="customerAddress" />
            </p>
            <p className="text-sm text-gray-700">
              <EditableText value={customerCityStateZip} field="customerCityStateZip" />
            </p>
          </div>
        </div>
      </div>
    );
  }

  // For other styles, render with simple text (can be extended later)
  return (
    <div className="p-8 bg-white">
      <p className="text-muted-foreground text-sm">
        Inline editing for {coverStyle} style coming soon. Use Quick Edit to update customer details.
      </p>
    </div>
  );
}
